import React from 'react';
import { X } from 'lucide-react';
import { CommentContent } from './CommentContent';
import { Button } from './ui/button';
import { useLocale } from '../contexts/LocaleContext';
import type { MentionableEntity } from './RichTextField';

type Comment = {
  id: string;
  documentId: string;
  userId: string;
  userEmail?: string;
  content: string;
  fromPos: number;
  toPos: number;
  selectedText: string;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
  replies?: Array<{
    id: string;
    userId: string;
    userEmail?: string;
    createdAt: string;
    comment: string;
  }>;
};

type CommentCardProps = {
  comment: Comment;
  onScrollToComment?: (comment: Comment) => void;
  onDeleteComment?: (commentId: string) => void;
  isMobileDrawer?: boolean;
  documentOwnerId?: string | null;
  onAddReply?: (commentId: string, content: string) => Promise<void>;
  onDeleteReply?: (commentId: string, replyId: string) => Promise<void>;
  onReplyDraftChange?: (hasDraft: boolean) => void;
  onDismiss?: () => void;
  showDeleteReplyButton?: boolean;
  commentThreadList?: 'active' | 'resolved';
  onUnresolveComment?: (commentId: string) => void;
  mentionableEntities?: MentionableEntity[];
};

export const CommentCard: React.FC<CommentCardProps> = ({
  comment,
  onScrollToComment,
  onDeleteComment,
  isMobileDrawer = false,
  documentOwnerId,
  onAddReply,
  onDeleteReply,
  onReplyDraftChange,
  onDismiss,
  showDeleteReplyButton = true,
  commentThreadList = 'active',
  onUnresolveComment,
  mentionableEntities = [],
}) => {
  const { t } = useLocale();

  return (
    <div
      style={{
        padding: isMobileDrawer ? '1rem' : '0.75rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem',
        position: 'relative',
        borderRadius: '0.5rem',
        backgroundColor: 'var(--card)',
        color: 'var(--card-foreground)',
        border: '1px solid var(--border)',
      }}
    >
      {onDismiss ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="absolute right-1 top-1 h-7 w-7"
          aria-label={t('close')}
          onClick={(e) => {
            e.stopPropagation();
            onDismiss();
          }}
        >
          <X className="h-4 w-4" />
        </Button>
      ) : null}
      <CommentContent
        comment={comment}
        onDeleteComment={onDeleteComment}
        onUnresolveComment={onUnresolveComment}
        showDeleteButton={true}
        documentOwnerId={documentOwnerId}
        onAddReply={onAddReply}
        onDeleteReply={onDeleteReply}
        onReplyDraftChange={onReplyDraftChange}
        onDismiss={onDismiss}
        showDeleteReplyButton={showDeleteReplyButton}
        commentThreadList={commentThreadList}
        mentionableEntities={mentionableEntities}
      />
    </div>
  );
};
